import React, { useMemo, useState } from 'react';
import type { CrudAddModalProps, CrudEditModalProps } from '../crud/CrudTable';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import useLoadingEffect from '@dr-pam/common-components/Hooks/useLoadingEffect';
import type { SubscriptionTypeOption } from '../../services/UpgradeService';
import { useUpgradeService } from '../../services/UpgradeService';
import { UpgradeDurationMode } from '../../graphql/graphql';
import type {
	UpgradePathDiscountCreateInput,
	UpgradePathDiscountListFragment,
	UpgradePathDiscountSingleFragment,
	UpgradePathDiscountUpdateInput,
} from '../../graphql/graphql';
import {
	ActionIcon,
	Alert,
	Button,
	Group,
	Input,
	Loader,
	NumberInput,
	Radio,
	Select,
	Stack,
	Switch,
	Text,
	Textarea,
	TextInput,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useForm, schemaResolver } from '@mantine/form';
import z from 'zod';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import { modals } from '@mantine/modals';

// This modal allows admins to create and edit upgrade paths (discount rules) without code changes.
// Every field maps directly to the Prisma model, giving marketing teams full control over:
// - Source and target products for upgrades (supports multiple target products with per-product CTAs)
// - Discount amounts (percentage or fixed) and trial credit application
// - Time windows (when discounts are available)
// - Priority ordering for multiple paths
// The modal includes validation to ensure at least one discount is configured and day windows are logical.
// When editing, it checks for invalid engagements if day windows change and offers cleanup options.
// Supports both legacy single-product paths (toProductId) and new many-to-many paths (targetProducts).
// Credit mode determines how prior payments are credited toward upgrades
type CreditMode = 'none' | 'single' | 'all';

const validationSchema = z
	.object({
		name: z.string().min(1, 'Name is required').max(512),
		description: z.string().max(2000).optional(),
		fromProductSubscriptionTypeId: z.string().min(1, 'Source subscription type is required'),
		discountPercentage: z.union([z.number().min(0).max(100), z.literal('')]),
		discountAmount: z.union([z.number().min(0), z.literal('')]),
		daysAfterPurchaseMin: z.union([z.number().min(0), z.literal('')]),
		daysAfterPurchaseMax: z.union([z.number().min(0), z.literal('')]),
		targetProducts: z
			.array(
				z.object({
					productSubscriptionTypeId: z.string().optional(),
				}),
			)
			.min(1, 'At least one target product is required')
			.refine(
				(products) =>
					products.some((p) => p.productSubscriptionTypeId && p.productSubscriptionTypeId.trim().length > 0),
				'At least one target product must be selected',
			),
		isActive: z.boolean(),
		creditMode: z.enum(['none', 'single', 'all']),
		upgradeDurationMode: z.nativeEnum(UpgradeDurationMode),
	})
	.refine(
		(data) => {
			const hasPercentage = typeof data.discountPercentage === 'number' && data.discountPercentage > 0;
			const hasAmount = typeof data.discountAmount === 'number' && data.discountAmount > 0;
			const hasCredit = data.creditMode !== 'none';
			return hasPercentage || hasAmount || hasCredit;
		},
		{
			message: 'Configure at least one discount or enable payment credit',
			path: ['discountPercentage'],
		},
	)
	.refine(
		(data) => {
			const min = typeof data.daysAfterPurchaseMin === 'number' ? data.daysAfterPurchaseMin : null;
			const max = typeof data.daysAfterPurchaseMax === 'number' ? data.daysAfterPurchaseMax : null;
			if (min !== null && max !== null) {
				return max >= min;
			}
			return true;
		},
		{
			message: 'Show until day must be greater than or equal to show from day',
			path: ['daysAfterPurchaseMax'],
		},
	);

type BaseModalProps = {
	mode: 'add' | 'edit';
};

type AddProps = CrudAddModalProps<UpgradePathDiscountListFragment, UpgradePathDiscountSingleFragment> & BaseModalProps;
type EditProps = CrudEditModalProps<UpgradePathDiscountListFragment, UpgradePathDiscountSingleFragment> &
	BaseModalProps;

type TargetProductFormValue = {
	productSubscriptionTypeId: string;
};

type FormValues = {
	name: string;
	description?: string;
	fromProductSubscriptionTypeId: string;
	discountPercentage: number | '';
	discountAmount: number | '';
	creditMode: CreditMode;
	daysAfterPurchaseMin: number | '';
	daysAfterPurchaseMax: number | '';
	targetProducts: TargetProductFormValue[]; // Multiple target subscription types
	isActive: boolean;
	upgradeDurationMode: UpgradeDurationMode;
};

export function AddUpgradePathModal(
	props: CrudAddModalProps<UpgradePathDiscountListFragment, UpgradePathDiscountSingleFragment>,
) {
	return <UpgradePathModal {...props} mode="add" />;
}

export function EditUpgradePathModal(
	props: CrudEditModalProps<UpgradePathDiscountListFragment, UpgradePathDiscountSingleFragment>,
) {
	return <UpgradePathModal {...props} mode="edit" />;
}

function UpgradePathModal(props: AddProps | EditProps) {
	const { onCancel, mode } = props;
	const current = mode === 'edit' && 'current' in props ? props.current : undefined;
	const upgradeService = useUpgradeService();
	const { addLoader, removeLoader, isLoading } = useLoadTracker();

	const [subscriptionTypes, setSubscriptionTypes] = useState<SubscriptionTypeOption[]>([]);
	const [fullCurrent, setFullCurrent] = useState<UpgradePathDiscountSingleFragment | undefined>(undefined);

	const isLoadingSubscriptionTypes = useLoadingEffect(async () => {
		try {
			const request = upgradeService.getSubscriptionTypes();
			const result = await request.response;
			setSubscriptionTypes(result);
		} catch (error) {
			NotificationUtils.showError(error as Error, 'Failed to load subscription types');
			setSubscriptionTypes([]);
		}
	}, [upgradeService]);

	const isLoadingFullData = useLoadingEffect(async () => {
		if (mode === 'edit' && current) {
			try {
				const request = upgradeService.getPath(current.id);
				const result = await request.response;
				setFullCurrent(result ?? undefined);
			} catch (error) {
				NotificationUtils.showError(error as Error, 'Failed to load upgrade path details');
				setFullCurrent(current as UpgradePathDiscountSingleFragment);
			}
		} else {
			setFullCurrent(undefined);
		}
	}, [mode, current?.id, upgradeService]);

	const initialValues = useMemo<FormValues>(() => {
		const dataToUse = fullCurrent ?? current;

		if (!dataToUse) {
			return {
				name: '',
				description: '',
				fromProductSubscriptionTypeId: '',
				discountAmount: '',
				discountPercentage: '',
				creditMode: 'single' as CreditMode, // Default to single payment credit
				daysAfterPurchaseMax: '',
				daysAfterPurchaseMin: 0,
				targetProducts: [{ productSubscriptionTypeId: '' }], // Start with one empty product
				isActive: true,
				upgradeDurationMode: UpgradeDurationMode.Refresh,
			};
		}

		// Extract target products from the path
		// Type assertion needed until GraphQL types include targetProducts
		const pathData = dataToUse as UpgradePathDiscountSingleFragment & {
			targetProducts?: Array<{ productSubscriptionTypeId: string }>;
		};
		const targetProducts =
			pathData.targetProducts?.map((tp) => ({
				productSubscriptionTypeId: tp.productSubscriptionTypeId,
			})) || [];

		// If no target products exist, start with one empty entry
		const initialTargetProducts = targetProducts.length > 0 ? targetProducts : [{ productSubscriptionTypeId: '' }];

		// Convert the two boolean fields to a single creditMode value
		// Priority: creditAllPriorPayments > applyCreditForExistingPurchase > none
		const getCreditMode = (): CreditMode => {
			if (dataToUse.creditAllPriorPayments) {
				return 'all';
			}
			if (dataToUse.applyCreditForExistingPurchase) {
				return 'single';
			}
			return 'none';
		};

		return {
			name: dataToUse.name,
			description: dataToUse.description ?? '',
			fromProductSubscriptionTypeId: dataToUse.fromProductSubscriptionTypeId || '',
			discountPercentage: dataToUse.discountPercentage ? Math.round(dataToUse.discountPercentage * 100) : '',
			discountAmount: dataToUse.discountAmountInCents ? dataToUse.discountAmountInCents / 100 : '',
			creditMode: getCreditMode(),
			daysAfterPurchaseMin: dataToUse.daysAfterPurchaseMin ?? 0,
			daysAfterPurchaseMax: dataToUse.daysAfterPurchaseMax ?? '',
			targetProducts: initialTargetProducts,
			isActive: dataToUse.isActive,
			upgradeDurationMode: dataToUse.upgradeDurationMode ?? UpgradeDurationMode.Refresh,
		};
	}, [current, fullCurrent]);

	// Only create form when we have the data we need
	const form = useForm<FormValues>({
		initialValues,
		validate: schemaResolver(validationSchema),
		validateInputOnChange: false,
		validateInputOnBlur: false,
	});

	const handleCancel = () => {
		onCancel?.();
	};

	const handleSubmit = async (values: FormValues) => {
		if (values.upgradeDurationMode === UpgradeDurationMode.Convert) {
			const shorterTargets = findShorterTargets(values, subscriptionTypes);
			if (shorterTargets.length > 0) {
				form.setFieldError(
					'targetProducts',
					`A Convert target plan cannot be shorter than the source plan: ${shorterTargets.join(', ')}`,
				);
				return;
			}
		}

		const loader = addLoader();
		try {
			if (mode === 'add') {
				const created = await upgradeService.createPath(buildCreateInput(values));
				if ('onCreated' in props) {
					props.onCreated(created);
				}
				NotificationUtils.showSuccess('Upgrade path created', created.name);
			} else if (current && 'onEdited' in props) {
				const oldMin = current.daysAfterPurchaseMin;
				const oldMax = current.daysAfterPurchaseMax;
				const newMin = typeof values.daysAfterPurchaseMin === 'number' ? values.daysAfterPurchaseMin : null;
				const newMax = typeof values.daysAfterPurchaseMax === 'number' ? values.daysAfterPurchaseMax : null;

				// When day windows change, check if existing engagements would become invalid.
				// This prevents data inconsistencies where users have engagements outside the new time window.
				const dayWindowChanged = oldMin !== newMin || oldMax !== newMax;

				if (dayWindowChanged) {
					try {
						const engagementCheck = await upgradeService.checkPathEngagements(
							current.id,
							newMin ?? undefined,
							newMax ?? undefined,
						);

						if (engagementCheck.invalidEngagements > 0) {
							modals.openConfirmModal({
								title: 'Day Window Changed - Invalid Engagements Detected',
								children: (
									<Stack gap="sm">
										<Text size="sm">
											Changing the day window will invalidate {engagementCheck.invalidEngagements}{' '}
											existing engagement(s).
										</Text>
										<Text size="sm" c="dimmed">
											Current window: {oldMin ?? 'any'} - {oldMax ?? 'any'} days
											<br />
											New window: {newMin ?? 'any'} - {newMax ?? 'any'} days
										</Text>
										<Alert color="yellow" title="What happens?">
											Invalid engagements will be automatically deleted. Users with invalid
											engagements will need to click the prompt again to create a new engagement.
										</Alert>
									</Stack>
								),
								labels: {
									confirm: 'Delete Invalid & Update',
									cancel: 'Cancel',
								},
								confirmProps: { color: 'orange' },
								onConfirm: async () => {
									try {
										const deleteResult = await upgradeService.deleteInvalidEngagements(
											current.id,
											newMin ?? undefined,
											newMax ?? undefined,
										);
										NotificationUtils.showSuccess('Engagements cleaned up', deleteResult.message);

										const updated = await upgradeService.updatePath(
											current.id,
											buildUpdateInput(values),
										);
										if ('onEdited' in props) {
											props.onEdited(updated);
										}
										NotificationUtils.showSuccess('Upgrade path updated', updated.name);
									} catch (error) {
										NotificationUtils.showError(error as Error, 'Failed to clean up engagements');
									}
								},
								onCancel: () => {},
							});
							return;
						}
					} catch (error) {
						NotificationUtils.showError(error as Error, 'Failed to check for invalid engagements');
						return;
					}
				}

				const updated = await upgradeService.updatePath(current.id, buildUpdateInput(values));
				if ('onEdited' in props) {
					props.onEdited(updated);
				}
				NotificationUtils.showSuccess('Upgrade path updated', updated.name);
			}
		} catch (error) {
			const errorMessage = (error as Error).message || 'Unknown error';
			NotificationUtils.showError(error as Error, `Failed to save upgrade path: ${errorMessage}`);
		} finally {
			removeLoader(loader);
		}
	};

	const convertTrialTargetNames = useMemo(() => {
		if (form.values.upgradeDurationMode !== UpgradeDurationMode.Convert) {
			return [];
		}
		const trialIds = new Set(subscriptionTypes.filter((st) => st.isTrialSubscription).map((st) => st.id));
		return form.values.targetProducts
			.filter((target) => trialIds.has(target.productSubscriptionTypeId))
			.map(
				(target) =>
					subscriptionTypes.find((st) => st.id === target.productSubscriptionTypeId)?.name ??
					target.productSubscriptionTypeId,
			);
	}, [form.values.upgradeDurationMode, form.values.targetProducts, subscriptionTypes]);

	const subscriptionTypeOptions = useMemo(
		() =>
			subscriptionTypes.map((st) => ({
				label: st.product ? `${st.product.shortName ?? st.product.name} — ${st.name}` : st.name,
				value: st.id,
			})),
		[subscriptionTypes],
	);

	const isFormLoading = isLoading || isLoadingSubscriptionTypes || isLoadingFullData;
	const disableForm = isFormLoading || subscriptionTypeOptions.length === 0;

	if (mode === 'edit' && isLoadingFullData) {
		return (
			<Stack gap="md" align="center" py="xl">
				<Loader size="lg" />
				<Text c="dimmed">Loading upgrade path details...</Text>
			</Stack>
		);
	}

	return (
		<form onSubmit={form.onSubmit(handleSubmit)}>
			<Stack gap="sm">
				<TextInput
					{...form.getInputProps('name')}
					label="Upgrade path name"
					withAsterisk
					disabled={disableForm}
				/>
				<Textarea
					{...form.getInputProps('description')}
					label="Description"
					autosize
					minRows={2}
					disabled={disableForm}
				/>
				<Select
					{...form.getInputProps('fromProductSubscriptionTypeId')}
					label="From Subscription Type"
					placeholder="Select source subscription type"
					description="The subscription type users must have to be eligible for this upgrade path"
					data={subscriptionTypeOptions}
					clearable
					searchable
					disabled={disableForm}
					maxDropdownHeight={300}
				/>
				<Group grow align="flex-end">
					<NumberInput
						{...form.getInputProps('discountPercentage')}
						label="Percentage discount"
						description="0 - 100%"
						min={0}
						max={100}
						disabled={disableForm}
						rightSection={<Text c="dimmed">%</Text>}
					/>
					<NumberInput
						{...form.getInputProps('discountAmount')}
						label="Fixed discount (AUD)"
						min={0}
						decimalScale={2}
						disabled={disableForm}
						leftSection={<Text c="dimmed">$</Text>}
					/>
				</Group>
				<Radio.Group
					label="Payment Credit Mode"
					description="How to calculate credit from prior payments toward this upgrade"
					value={form.values.creditMode}
					onChange={(value) => form.setFieldValue('creditMode', value as CreditMode)}
				>
					<Stack gap="xs" mt="xs">
						<Radio
							value="none"
							label="No credit"
							description="No prior payment credit applied"
							disabled={disableForm}
						/>
						<Radio
							value="single"
							label="Credit most recent payment"
							description="Applies the user's most recent successful payment as credit"
							disabled={disableForm}
						/>
						<Radio
							value="all"
							label="Credit ALL prior payments"
							description="Sums all prior payments as credit. Intended for legacy subscription types with recurring payments."
							disabled={disableForm}
						/>
					</Stack>
				</Radio.Group>

				<Radio.Group
					label="Duration Mode"
					description="How the new plan's dates are set when someone takes this path"
					value={form.values.upgradeDurationMode}
					onChange={(value) => form.setFieldValue('upgradeDurationMode', value as UpgradeDurationMode)}
				>
					<Stack gap="xs" mt="xs">
						<Radio
							value={UpgradeDurationMode.Refresh}
							label="Refresh"
							description="Full new duration, starting from the moment of the change"
							disabled={disableForm}
						/>
						<Radio
							value={UpgradeDurationMode.Convert}
							label="Convert"
							description="Keeps the original start date, so the new plan covers the remainder of the term already paid for"
							disabled={disableForm}
						/>
					</Stack>
				</Radio.Group>

				{convertTrialTargetNames.length > 0 && (
					<Alert color="yellow" variant="light">
						Convert is ignored for trial subscription types: {convertTrialTargetNames.join(', ')}{' '}
						{convertTrialTargetNames.length === 1 ? 'behaves' : 'behave'} as Refresh.
					</Alert>
				)}

				<Group grow align="flex-start">
					<NumberInput
						{...form.getInputProps('daysAfterPurchaseMin')}
						label="Show from day"
						min={0}
						disabled={disableForm}
					/>
					<NumberInput
						{...form.getInputProps('daysAfterPurchaseMax')}
						label="Show until day"
						min={0}
						placeholder="No limit"
						disabled={disableForm}
					/>
				</Group>
				{/* Multiple Target Subscription Types Section - Each subscription type can have its own CTA text */}
				<Stack gap="sm">
					<Group justify="space-between" align="center">
						<Text fw={500} size="sm">
							Target Subscription Types
						</Text>
						<Button
							size="xs"
							variant="light"
							leftSection={<IconPlus size={14} />}
							onClick={(e) => {
								e.stopPropagation();
								const newProducts = [...form.values.targetProducts, { productSubscriptionTypeId: '' }];
								form.setValues({
									...form.values,
									targetProducts: newProducts,
								});
							}}
							disabled={disableForm}
						>
							Add Subscription Type
						</Button>
					</Group>
					<Stack gap="sm">
						{form.values.targetProducts.map((targetProduct, index) => (
							<Group key={index} align="flex-start" gap="sm">
								<Select
									{...form.getInputProps(`targetProducts.${index}.productSubscriptionTypeId`)}
									data={subscriptionTypeOptions}
									label={`To Subscription Type ${index + 1}`}
									placeholder="Select subscription type"
									disabled={disableForm}
									searchable
									maxDropdownHeight={300}
									style={{ flex: 1 }}
								/>
								<ActionIcon
									color="red"
									variant="subtle"
									mt={28}
									onClick={(e) => {
										e.stopPropagation();
										if (form.values.targetProducts.length > 1) {
											const newProducts = form.values.targetProducts.filter(
												(_, i) => i !== index,
											);
											form.setValues({
												...form.values,
												targetProducts: newProducts,
											});
										}
									}}
									disabled={disableForm || form.values.targetProducts.length <= 1}
									title={
										form.values.targetProducts.length <= 1
											? 'At least one subscription type is required'
											: 'Remove subscription type'
									}
								>
									<IconTrash size={16} />
								</ActionIcon>
							</Group>
						))}
					</Stack>
					{typeof form.errors.targetProducts === 'string' && (
						<Input.Error>{form.errors.targetProducts}</Input.Error>
					)}
				</Stack>
				{!subscriptionTypeOptions.length && !isFormLoading && (
					<Alert color="yellow" variant="light">
						You need at least one subscription type before configuring an upgrade path.
					</Alert>
				)}
				<Switch
					label="Path is active"
					checked={form.values.isActive}
					onChange={(event) => form.setFieldValue('isActive', event.currentTarget.checked)}
					disabled={disableForm}
				/>
				<Group justify="flex-end" mt="md">
					<Button variant="subtle" type="button" onClick={handleCancel} disabled={isFormLoading}>
						Cancel
					</Button>
					<Button type="submit" disabled={disableForm}>
						Save {isFormLoading && <Loader ml="xs" size="sm" />}
					</Button>
				</Group>
			</Stack>
		</form>
	);
}

function findShorterTargets(values: FormValues, subscriptionTypes: SubscriptionTypeOption[]): string[] {
	const durationById = new Map(subscriptionTypes.map((st) => [st.id, st.durationInDays]));
	const sourceDuration = durationById.get(values.fromProductSubscriptionTypeId);

	if (typeof sourceDuration !== 'number') {
		return [];
	}

	return values.targetProducts
		.filter((target) => {
			const targetDuration = durationById.get(target.productSubscriptionTypeId);
			return typeof targetDuration === 'number' && targetDuration < sourceDuration;
		})
		.map(
			(target) =>
				subscriptionTypes.find((st) => st.id === target.productSubscriptionTypeId)?.name ??
				target.productSubscriptionTypeId,
		);
}

function buildCreateInput(values: FormValues): UpgradePathDiscountCreateInput {
	const normalizedDescription = values.description?.trim();

	// Build target products - always required (multiple subscription types)
	// Deduplicate target products to prevent unique constraint violations
	const uniqueTargetProducts = values.targetProducts
		.filter((tp) => tp.productSubscriptionTypeId) // Filter out empty entries
		.filter(
			(tp, index, arr) =>
				arr.findIndex((t) => t.productSubscriptionTypeId === tp.productSubscriptionTypeId) === index,
		);

	const targetProductsInput = {
		create: uniqueTargetProducts.map((tp) => ({
			productSubscriptionType: { connect: { id: tp.productSubscriptionTypeId } },
		})),
	};

	// Convert creditMode to the two boolean fields for the database
	const applyCreditForExistingPurchase = values.creditMode === 'single';
	const creditAllPriorPayments = values.creditMode === 'all';

	const input = {
		name: values.name,
		description: normalizedDescription ? normalizedDescription : undefined,
		// Connect to ProductSubscriptionType - this is the subscription type users must have to be eligible
		fromProductSubscriptionType: {
			connect: { id: values.fromProductSubscriptionTypeId },
		},
		targetProducts: targetProductsInput,
		discountPercentage: typeof values.discountPercentage === 'number' ? values.discountPercentage / 100 : undefined,
		discountAmountInCents:
			typeof values.discountAmount === 'number' ? Math.round(values.discountAmount * 100) : undefined,
		applyCreditForExistingPurchase,
		creditAllPriorPayments,
		daysAfterPurchaseMin: typeof values.daysAfterPurchaseMin === 'number' ? values.daysAfterPurchaseMin : undefined,
		daysAfterPurchaseMax: typeof values.daysAfterPurchaseMax === 'number' ? values.daysAfterPurchaseMax : undefined,
		isActive: values.isActive,
		upgradeDurationMode: values.upgradeDurationMode,
	};

	return input as UpgradePathDiscountCreateInput;
}

function buildUpdateInput(values: FormValues): UpgradePathDiscountUpdateInput {
	const normalizedDescription = values.description?.trim() ?? '';

	// Build target products update - delete all existing and create new ones
	// deleteMany: {} deletes all related records
	// Deduplicate target products to prevent unique constraint violations
	const uniqueTargetProducts = values.targetProducts
		.filter((tp) => tp.productSubscriptionTypeId) // Filter out empty entries
		.filter(
			(tp, index, arr) =>
				arr.findIndex((t) => t.productSubscriptionTypeId === tp.productSubscriptionTypeId) === index,
		); // Remove duplicates

	const targetProductsUpdate = {
		deleteMany: {}, // Delete all existing target products
		create: uniqueTargetProducts.map((tp) => ({
			productSubscriptionType: { connect: { id: tp.productSubscriptionTypeId } },
		})),
	};

	// Convert creditMode to the two boolean fields for the database
	const applyCreditForExistingPurchase = values.creditMode === 'single';
	const creditAllPriorPayments = values.creditMode === 'all';

	const input = {
		name: { set: values.name },
		description: { set: normalizedDescription.length ? normalizedDescription : null },
		// Connect to ProductSubscriptionType - this is the subscription type users must have to be eligible
		fromProductSubscriptionType: { connect: { id: values.fromProductSubscriptionTypeId } },
		targetProducts: targetProductsUpdate,
		discountPercentage: {
			set: typeof values.discountPercentage === 'number' ? values.discountPercentage / 100 : null,
		},
		discountAmountInCents: {
			set: typeof values.discountAmount === 'number' ? Math.round(values.discountAmount * 100) : null,
		},
		applyCreditForExistingPurchase: { set: applyCreditForExistingPurchase },
		creditAllPriorPayments: { set: creditAllPriorPayments },
		daysAfterPurchaseMin: {
			set: typeof values.daysAfterPurchaseMin === 'number' ? values.daysAfterPurchaseMin : null,
		},
		daysAfterPurchaseMax: {
			set: typeof values.daysAfterPurchaseMax === 'number' ? values.daysAfterPurchaseMax : null,
		},
		isActive: { set: values.isActive },
		upgradeDurationMode: { set: values.upgradeDurationMode },
	};

	return input as UpgradePathDiscountUpdateInput;
}
